import React, { useEffect } from 'react';
import Loading from '../../common/components/Loading';
import { Truck } from '../../models/truck';
import { Location } from '../../locations/locationsModels';
import { Button, TextField } from '@cimpress/react-components';
import DailyScheduleComponent from './DailyScheduleComponent';
import NewTruckFormContainer from '../../trucks/components/NewTruckFormContainer';
import NewTruckFormContainerV2 from '../../trucks/components/NewTruckFormContainer.v2';

import TruckContainer from '../../trucks/components/TruckContainer';
import { getTruckManifestStatus, TruckManifestStatus } from '../../trucks/trucksUtils';
import TruckCountComponent from './TruckCountComponent';
import { MatchType } from '../../trucks/components/TruckManifestComponent';
import { ModalAction } from '../../contexts/modal-context';
import { DateString } from '../../models/utils';
import { FeatureToggle } from '../../components/utils/FeatureToggle';
import { useHistory } from 'react-router';
import { Features } from '../../models/features';
import { FlexBox, FlexWrap, AlignItems, JustifyContent } from '../../utils/css-classes';

interface Props {
  selectedLocation: Location;
  selectedDate: DateString;
  trucks: Truck[];
  isFetchingTrucks: boolean;
  isFetchingLocations: boolean;
  changeSelectedDate: (selectedDate: DateString) => any;
  showNewTruck: (locationKey: string) => any;
  getTrucks: (selectedDate: DateString) => any;
  editTruckId?: string;
  loadTruckId?: string;
  manifestTruckId?: string;
  autoRefresh: boolean;
  getLinehaulCompanies: (locationKey: string) => any;
  getCustomsBrokers: (locationKey: string) => any;
  getTruckLoadingRestrictions: (locationKey: string) => void;
  getTruckTemplates: () => void;
  modal: ModalAction;
}

enum ShowManifestStatus {
  ready = 0b00001,
  inProgress = 0b00010,
  success = 0b00100,
  error = 0b01000,
  userActionNeeded = 0b10000,
  all = 0b11111,
}

const sortTrucks = ([{}, truckA]: [number, Truck], [{}, truckB]: [number, Truck]) => {
  if (truckA.closedAt) {
    if (truckB.closedAt) {
      return truckB.closedAt.localeCompare(truckA.closedAt);
    }
    return -1;
  }
  if (truckB.closedAt) {
    return 1;
  }
  return truckB.createdAt.localeCompare(truckA.createdAt);
};

// tslint:disable-next-line:function-name
export default function ManifestingPage({
  selectedLocation,
  selectedDate,
  trucks,
  isFetchingTrucks,
  isFetchingLocations,
  changeSelectedDate,
  getTrucks,
  autoRefresh,
  getLinehaulCompanies,
  getCustomsBrokers,
  getTruckLoadingRestrictions,
  getTruckTemplates,
  modal,
}: Props) {
  const history = useHistory();
  const [filters, setFilters] = React.useState(ShowManifestStatus.all);
  const toggleFilter = (category: ShowManifestStatus) => setFilters(filters ^ category);
  const doShowCategory = (category: ShowManifestStatus) => Boolean(filters & category);

  const [filterValue, setFilterValue] = React.useState('');
  const filter = function filter(event: React.ChangeEvent<HTMLInputElement>) {
    setFilterValue(event.currentTarget.value);
  };

  useEffect(
    () => {
      if (selectedLocation && selectedDate) {
        getTrucks(selectedDate);
      }
    },
    [selectedLocation, isFetchingLocations, selectedDate],
  );

  useEffect(
    () => {
      if (!autoRefresh || !selectedLocation || !selectedDate) {
        return;
      }
      const timeout = setTimeout(
        () => getTrucks(selectedDate),
        5000,
      );
      return () => clearTimeout(timeout);
    },
    [autoRefresh, trucks],
  );

  useEffect(
    () => {
      getLinehaulCompanies(selectedLocation.key);
      getCustomsBrokers(selectedLocation.key);
      getTruckLoadingRestrictions(selectedLocation.key);
      getTruckTemplates();
    },
    [selectedLocation],
  );

  if (isFetchingLocations || !selectedLocation || !selectedDate) {
    return <Loading />;
  }

  const trucksByStatus: { [status: string]: Truck[] } = {};
  (trucks || [])
    .slice()
    .forEach(truck => {
      const truckManifestStatus = getTruckManifestStatus(truck);
      if (!trucksByStatus[truckManifestStatus]) {
        trucksByStatus[truckManifestStatus] = [];
      }
      trucksByStatus[truckManifestStatus].push(truck);
    });

  function renderBody() {
    if (!autoRefresh && isFetchingTrucks) {
      return (
        <Loading />
      );
    }

    if (trucks && trucks.length === 0) {
      return (
        <p className="text-center">There are no trucks for the selected date.</p>
      );
    }

    if (trucks) {
      return trucks
        .filter(truck => filters & ShowManifestStatus[getTruckManifestStatus(truck)])
        .map(truck => [filterTruck(truck), truck] as [number, Truck])
        .filter(([matchType, _]) => filterValue === '' || matchType > 0)
        .sort(sortTrucks)
        .map(([matchType, truck]) => renderTruckContainer(truck, matchType));
    }
  }

  const filterTruck = function filterTruck(truck: Truck) {
    let matchType = MatchType.NO_MATCH;
    if (filterValue === '') {
      return matchType;
    }

    const re = new RegExp(filterValue, 'i');
    if (truck.palletBoxes) {
      for (const palletBox of truck.palletBoxes) {
        if (`${palletBox.externalId} ${palletBox.name}`.search(re) >= 0) {
          matchType |= MatchType.PALLET_BOX;
          break;
        }
      }
    }

    if (truck.manifest && truck.manifest.documents) {
      for (const document of truck.manifest.documents) {
        if (document.fileName.search(re) >= 0) {
          matchType |= document.carrierAccountKey === null
            ? MatchType.BORDER_DOCUMENT
            : MatchType.CARRIER_DOCUMENT;
        }
      }
    }
    return matchType;
  };

  function renderTruckContainer(truck: Truck, matchType: number) {
    return <TruckContainer
      key={truck.id}
      truck={truck}
      selectedLocation={selectedLocation}
      matchType={matchType}
      highlight={filterValue === '' ? undefined : filterValue}
    />;
  }

  const categories: [TruckManifestStatus, ShowManifestStatus][] = [
    [TruckManifestStatus.Ready, ShowManifestStatus.ready],
    [TruckManifestStatus.UserActionNeeded, ShowManifestStatus.userActionNeeded],
    [TruckManifestStatus.InProgress, ShowManifestStatus.inProgress],
    [TruckManifestStatus.Error, ShowManifestStatus.error],
    [TruckManifestStatus.Success, ShowManifestStatus.success],
  ];

  const widthStyle = { width: '5%' };

  const showNewTruck = () =>
    modal(
      <h4 key="h4">{'New Truck'}</h4>,
      hide => <>
        <FeatureToggle whenFlag={Features.CUSTOMS_BROKER_AOT} isEnabled>
          <NewTruckFormContainerV2 hide={hide} />
        </FeatureToggle>
        <FeatureToggle whenFlag={Features.CUSTOMS_BROKER_AOT} isDisabled>
          <NewTruckFormContainer hide={hide} />
        </FeatureToggle>
      </>,
      { bsStyle: 'info' },
    );

  return (
    <div className="">
      <div className="page-title">
        <div className="h1">Truck Manifests</div>
        <p>
          See an overview of trucks loaded with shipment pallets and view their manifests.
          Add on-demand trucks as needed.
        </p>
      </div>
      <div className={`${FlexBox.FLEX} ${FlexWrap.WRAP} ${AlignItems.CENTER} ${JustifyContent.CENTER} summary`}>
        {
          isFetchingTrucks ||
          categories
            .filter(([status, _]) => trucksByStatus[status])
            .map(([status, filter]) =>
              <TruckCountComponent
                count={trucksByStatus[status].length}
                type={status}
                disabled={doShowCategory(filter)}
                onClick={() => toggleFilter(filter)}
                key={status}
              />,
            )
        }
      </div>
      <div
        className={`${FlexBox.FLEX} ${JustifyContent.SPACE_BETWEEN} ${AlignItems.CENTER} py-3 my-3 manifests-header`}
      >
        <div className={`${FlexBox.FLEX} ${AlignItems.FLEX_START}`} style={widthStyle}>
          <DailyScheduleComponent
            selectedLocation={selectedLocation!}
            selectedDate={selectedDate}
            changeSelectedDate={changeSelectedDate}
          />
          <TextField
            label="Search"
            value={filterValue}
            onChange={filter}
            className="filter-textfield"
          />
        </div>
        <div>
        </div>
        <div className={`${FlexBox.FLEX} ${JustifyContent.FLEX_END}`} style={widthStyle}>
          <FeatureToggle whenFlag={Features.SHOW_SETTINGS} isEnabled={true} >
            <Button
              className="btn btn-default max-height-36 min-width-180"
              style={{ marginRight: '10px' }}
              onClick={() => history.push('/settings/templates')}
            >
              Edit templates
            </Button>
          </FeatureToggle>
          <Button
            className="btn btn-default max-height-36 min-width-180"
            type="primary"
            onClick={() => showNewTruck()}
          >
            Add Truck
          </Button>
        </div>
      </div>
      {renderBody()}
    </div>
  );
}
